import type { ReactNode } from 'react';

export interface GlowProps {
  color?: 'blue' | 'gold';
  size?: number;
  opacity?: number;
  className?: string;
  blur?: number;
}

/** Ambient-свечение секции. */
export function Glow({ color = 'blue', size = 460, opacity = 0.16, blur = 70, className = '' }: GlowProps) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute rounded-full ${className}`}
      style={{
        width: size,
        height: size,
        background: color === 'blue' ? '#2B6BFF' : '#C9A24D',
        opacity,
        filter: `blur(${blur}px)`,
      }}
    />
  );
}

/** Глянцевый «орб» из макета (синий шар с бликами). */
export function Orb({ className = '' }: { className?: string }) {
  const positioned = /(^|\s)absolute(\s|$)/.test(className);
  return (
    <div aria-hidden className={`rounded-full ${positioned ? '' : 'relative'} ${className}`}>
      <div className="absolute inset-0 rounded-full bg-accent-gradient-sky" />
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(50% 50% at 72% 72%, rgba(31,79,209,0.8) 0%, rgba(31,79,209,0) 100%)',
        }}
      />
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(50% 50% at 34% 55%, rgba(122,211,255,0.95) 0%, rgba(122,211,255,0) 100%)',
        }}
      />
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(50% 50% at 40% 38%, rgba(227,201,138,0.8) 0%, rgba(227,201,138,0) 100%)',
        }}
      />
      <div
        className="absolute inset-0 rounded-full opacity-75"
        style={{
          background:
            'radial-gradient(50% 50% at 34% 28%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
        }}
      />
      <div className="absolute inset-[14%] rounded-full border-[min(10px,2.5vw)] border-white/60" />
    </div>
  );
}

export type PillTone = 'blue' | 'green' | 'white' | 'dark';

export function StatusPill({
  children,
  tone = 'blue',
  className = '',
}: {
  children: ReactNode;
  tone?: PillTone;
  className?: string;
}) {
  const tones: Record<PillTone, { wrap: string; dot: string }> = {
    blue: { wrap: 'bg-accent-soft text-accent', dot: 'bg-accent' },
    green: { wrap: 'bg-success-soft text-success', dot: 'bg-success' },
    white: { wrap: 'bg-white text-accent', dot: 'bg-accent' },
    dark: { wrap: 'bg-white/10 text-white', dot: 'bg-success' },
  };
  const current = tones[tone];
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-[13px] font-semibold leading-[18px] md:text-sm ${current.wrap} ${className}`}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${current.dot}`} />
      {children}
    </span>
  );
}

export function Chip({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-[10px] border border-line bg-white px-4 py-2.5 text-[14px] font-medium text-ink md:text-[15px] ${className}`}
    >
      {children}
    </span>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  subtitle,
  tone = 'light',
  className = '',
}: {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  tone?: 'light' | 'dark';
  className?: string;
}) {
  const isDark = tone === 'dark';
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <span
        className={`text-[12px] font-semibold uppercase tracking-[0.08em] md:text-[13px] ${
          isDark ? 'text-gold-300' : 'text-accent'
        }`}
      >
        {eyebrow}
      </span>
      <h2
        className={`max-w-[920px] text-[30px] font-semibold leading-[1.12] tracking-[-0.025em] md:text-[42px] lg:text-[52px] ${
          isDark ? 'text-white' : 'text-ink'
        }`}
      >
        {title}
      </h2>
      {subtitle ? (
        <p
          className={`max-w-[690px] text-[16px] leading-[1.55] md:text-[19px] ${
            isDark ? 'text-muted-sky' : 'text-muted'
          }`}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

export function Emblem({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 36 36"
      fill="none"
      aria-hidden
      className={className}
      width={36}
      height={36}
    >
      <rect width="36" height="36" rx="10" fill="#0A1B33" />
      <path
        d="M18 7.5l8.5 3.8v6.2c0 4.7-3.5 8.2-8.5 10.5-5-2.3-8.5-5.8-8.5-10.5v-6.2L18 7.5Z"
        stroke="#C9A24D"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12.8 15.6h10.4M18 13v7.5" stroke="#C9A24D" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12.8 15.6 11 19.4h3.6l-1.8-3.8ZM23.2 15.6 21.4 19.4H25l-1.8-3.8Z" fill="#C9A24D" />
    </svg>
  );
}
